import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ExternalLink, FileText, Play, Calendar, Globe, ShieldCheck, ChevronDown, Package } from 'lucide-react';
import { ProductPlaceholder } from '../components/ProductPlaceholder';
import { getShopUrl } from '../data/shopUrls';
import { LogoMark } from '../components/LogoMark';
import { PartnerCTA } from '../components/PartnerCTA';
export function HomePage() {
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);
  const videoUrl = ''; // Placeholder for team to add URL later
  const toggleFaq = (index: number) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };
  const faqs = [{
    q: 'Who is Chiltern Global?',
    a: 'Chiltern Global is a specialist supplier supporting defence, police, prisons, medical and security procurement. We work with established manufacturing partners and structured delivery processes to support operational requirements.'
  }, {
    q: 'What is the best procurement route for my organisation?',
    a: 'Procurement routes vary by sector, requirement and purchasing framework. If you share your organisation type and scope, we will advise the most suitable route and the supporting information required for review.'
  }, {
    q: 'Can you support larger volumes or programme requirements?',
    a: 'Yes. For larger quantities or programme-based requirements, we will confirm specification alignment, fulfilment approach and documentation availability as part of our review process.'
  }];
  const highlightProducts = [{
    name: 'Public Order Shield',
    desc: 'Polycarbonate interlocking shield for crowd control and cell entry operations.',
    href: 'https://shop.chilternglobal.com/products/public-order-shield'
  }, {
    name: 'Tactical Vest',
    desc: 'Modular plate carrier for ballistic protection in high-mobility roles.',
    href: 'https://shop.chilternglobal.com/products/tactical-vest'
  }, {
    name: 'Patrol / Public Order Boot',
    desc: 'Durable operational footwear for patrol and public order deployments.',
    href: 'https://shop.chilternglobal.com/products/patrol-public-order-boot'
  }];
  const markets = [{
    name: 'Police and Prisons',
    desc: 'Supporting law enforcement and custodial services with public order, patrol, and restraint equipment. Manufactured to Home Office and Ministry of Justice technical specifications.',
    image: 'https://cdn.magicpatterns.com/uploads/njGsCZ1bVyNr1h2qALmbXq/banner-img.jpg',
    to: '/manufacturing/markets#police'
  }, {
    name: 'Military',
    desc: 'Mission-critical equipment for armed forces and defence contractors. Compliant with DEF STAN requirements and available via JOSCAR-registered supply chains.',
    image: 'https://cdn.magicpatterns.com/uploads/cgpFBudHGosue3cH628Vuv/45161938-scaled.jpg',
    to: '/manufacturing/markets#defence'
  }, {
    name: 'Medical',
    desc: 'Textile-based medical equipment and protective wear for healthcare providers and emergency services. Aligned to infection control and clinical operational standards.',
    image: 'https://cdn.magicpatterns.com/uploads/cJmcybkRNgCksh1S8QiVuQ/What_we_do.jpg',
    to: '/manufacturing/markets#medical'
  }, {
    name: 'Security',
    desc: 'Operational gear for private security and facilities management. Providing durable, professional equipment for manned guarding and event security roles.',
    image: 'https://cdn.magicpatterns.com/uploads/sK8ZMn89EawgZpsPCJMPQz/Riot-and-Protection.jpg',
    to: '/manufacturing/markets'
  }];
  const newsItems = [{
    title: 'Chiltern Global expands manufacturing capabilities',
    excerpt: 'New production lines commissioned to support increasing demand for tactical load carriage systems.',
    image: 'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=800&q=80',
    date: 'Latest Update'
  }, {
    title: 'Supply chain resilience in defence procurement',
    excerpt: 'How we are strengthening logistics networks to ensure continuity of supply for critical equipment.',
    image: 'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=800&q=80',
    date: 'Industry Insight'
  }, {
    title: 'New framework appointments announced',
    excerpt: 'Chiltern Global secures place on key public sector frameworks for protective clothing and equipment.',
    image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=800&q=80',
    date: 'Company News'
  }];
  return <>
      {/* 1. Hero Section */}
      <section className="relative min-h-[360px] flex items-center pt-32 pb-14 md:pt-36 md:pb-20" data-id="element-891">
        <div className="absolute inset-0 z-0" data-id="element-892">
          <img src="https://cdn.magicpatterns.com/uploads/e7aUvLm1nf7EFY2JtRJTkW/Home_Page.jpg" alt="Operational Context" className="w-full h-full object-cover" data-id="element-893" />
          <div className="absolute inset-0 bg-gradient-to-r from-navy-900/95 via-navy-900/80 to-navy-900/40" data-id="element-894" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 w-full" data-id="element-895">
          <div className="max-w-3xl" data-id="element-896">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight" data-id="element-897">
              Specialist Manufacturing Partners.
            </h1>
            <p className="text-lg text-steel-300 mb-8 leading-relaxed max-w-2xl font-light" data-id="element-898">
              The chosen supplier for protecting those who protect us. Market
              leading manufacturers of specialist equipment for the defence,
              police, prison, and medical sectors since 2005.
            </p>
            <div className="flex flex-col sm:flex-row gap-5" data-id="element-899">
              <Link to="/manufacturing/markets" className="inline-flex items-center justify-center px-10 py-4 bg-cg-orange-600 hover:bg-cg-orange-500 text-white text-sm font-bold uppercase tracking-widest rounded-sm transition-colors" data-id="element-900">
                Our Markets
              </Link>
              <Link to="/manufacturing/frameworks" className="inline-flex items-center justify-center px-10 py-4 border border-white/30 hover:bg-white/10 text-white text-sm font-bold uppercase tracking-widest rounded-sm transition-colors" data-id="element-901">
                View Frameworks
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* 2. Video + Context (Two-Column) */}
      <section className="bg-white py-20 md:py-24" data-id="element-902">
        <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-903">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center" data-id="element-904">
            {/* Left: Video (55%) */}
            <div className="lg:col-span-7" data-id="element-905">
              <div className="relative aspect-video bg-navy-900 rounded-sm overflow-hidden shadow-xl" data-id="element-906">
                {isVideoPlaying && videoUrl ? <iframe src={videoUrl} className="w-full h-full" title="Chiltern Global Introduction" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen data-id="element-907"></iframe> : <div className="absolute inset-0 flex items-center justify-center" data-id="element-908">
                    <button onClick={() => setIsVideoPlaying(true)} className="group relative flex items-center justify-center w-20 h-20 bg-cg-orange-600 rounded-full hover:bg-cg-orange-500 transition-all duration-300 hover:scale-110 focus:outline-none focus:ring-4 focus:ring-cg-orange-600/30" aria-label="Play video" data-id="element-909">
                      <Play className="w-8 h-8 text-white fill-current ml-1" data-id="element-910" />
                      <div className="absolute inset-0 rounded-full animate-ping bg-cg-orange-600 opacity-20" data-id="element-911"></div>
                    </button>
                  </div>}
              </div>
            </div>

            {/* Right: Context (45%) */}
            <div className="lg:col-span-5" data-id="element-912">
              <span className="text-cg-orange-600 font-bold uppercase tracking-widest text-sm mb-3 block" data-id="element-913">
                About Chiltern Global
              </span>
              <h2 className="text-3xl font-bold text-navy-900 mb-6 leading-tight" data-id="element-914">
                What we do
              </h2>
              <p className="text-steel-600 leading-relaxed mb-4" data-id="element-915">
                Chiltern Global is a specialist supplier supporting defence,
                police, prisons and medical procurement teams. We work with
                established manufacturing partners to source, produce and
                deliver operational equipment across a range of categories, with
                offices in the United Kingdom and the United Arab Emirates.
              </p>
              <p className="text-steel-600 leading-relaxed mb-8" data-id="element-916">
                Our team supports procurement review with structured
                documentation, sector-specific catalogues and category guidance
                to help organisations evaluate and order with confidence.
              </p>
              <div className="flex flex-col sm:flex-row gap-4" data-id="element-917">
                <Link to="/manufacturing/processes" className="inline-flex items-center justify-center px-8 py-3.5 bg-navy-900 hover:bg-cg-orange-600 text-white text-sm font-bold uppercase tracking-widest rounded-sm transition-colors" data-id="element-918">
                  Explore Manufacturing
                </Link>
                <Link to="/contact" className="inline-flex items-center justify-center px-8 py-3.5 border border-navy-900 text-navy-900 hover:bg-navy-900 hover:text-white text-sm font-bold uppercase tracking-widest rounded-sm transition-colors" data-id="element-919">
                  Contact Us
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 3. Highlight of the Week */}
      <section className="bg-slate-50 py-24 border-t border-steel-100" data-id="element-920">
        <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-921">
          <div className="mb-12" data-id="element-922">
            <span className="text-cg-orange-600 font-bold uppercase tracking-widest text-sm mb-2 block" data-id="element-923">
              Featured This Week
            </span>
            <h2 className="text-3xl font-bold text-navy-900" data-id="element-924">
              Highlight of the Week
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-925">
            {highlightProducts.map((product, index) => <div key={index} className="bg-white border border-steel-200 rounded-sm overflow-hidden flex flex-col hover:border-cg-orange-600 transition-colors group" data-id="element-926">
                <div className="aspect-square bg-slate-100" data-id="element-927">
                  <ProductPlaceholder aspectRatio="1/1" className="w-full h-full" data-id="element-928" />
                </div>
                <div className="p-6 flex flex-col flex-1" data-id="element-929">
                  <h3 className="text-lg font-bold text-navy-900 mb-2 group-hover:text-cg-orange-600 transition-colors" data-id="element-930">
                    {product.name}
                  </h3>
                  <p className="text-sm text-steel-600 mb-6 flex-1" data-id="element-931">
                    {product.desc}
                  </p>
                  <a href={product.href} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm font-bold text-cg-orange-600 uppercase tracking-wider hover:text-navy-900 transition-colors" data-id="element-932">
                    View in Inventory <ExternalLink className="w-3 h-3" data-id="element-933" />
                  </a>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* Markets we support */}
      <section className="bg-white py-24 border-t border-steel-100" data-id="element-934">
        <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-935">
          <div className="mb-12 max-w-3xl" data-id="element-936">
            <h2 className="text-3xl font-bold text-navy-900 mb-4" data-id="element-937">
              Markets we support
            </h2>
            <p className="text-lg text-steel-600 leading-relaxed" data-id="element-938">
              Procurement-led support across operational environments and
              specialist requirements.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-939">
            {markets.map((market, index) => <Link key={index} to={market.to} className="group flex flex-col bg-white border border-steel-200 hover:border-cg-orange-600 rounded-sm overflow-hidden transition-all duration-300 shadow-sm hover:shadow-md h-full" data-id="element-940">
                {/* Image Area - 60% */}
                <div className="relative h-[240px] overflow-hidden bg-navy-900" data-id="element-941">
                  <img src={market.image} alt={market.name} className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500" data-id="element-942" />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy-900/80 to-transparent opacity-60" data-id="element-943" />
                </div>

                {/* Text Area - 40% */}
                <div className="p-6 flex flex-col flex-1 bg-white" data-id="element-944">
                  <h3 className="text-lg font-bold text-navy-900 mb-3 group-hover:text-cg-orange-600 transition-colors" data-id="element-945">
                    {market.name}
                  </h3>
                  <p className="text-xs text-steel-600 leading-relaxed mb-6 flex-1 line-clamp-4" data-id="element-946">
                    {market.desc}
                  </p>
                  <div className="mt-auto pt-4 border-t border-steel-100 flex items-center justify-between" data-id="element-947">
                    <span className="text-xs font-bold uppercase tracking-wider text-navy-900 group-hover:text-cg-orange-600 transition-colors" data-id="element-948">
                      Explore this market
                    </span>
                    <ArrowRight className="w-3.5 h-3.5 text-cg-orange-600 transform group-hover:translate-x-1 transition-transform" data-id="element-949" />
                  </div>
                </div>
              </Link>)}
          </div>
        </div>
      </section>

      {/* 4. Explore the full Inventory — Premium Banner (now includes catalogue CTA) */}
      <section className="relative bg-navy-900 py-20 md:py-28 overflow-hidden" data-id="element-950">
        <div className="absolute inset-0 z-0" data-id="element-951">
          <img src="https://cdn.magicpatterns.com/uploads/rnUnJ6Ay6D84jn5vRbVj6B/Bags-and-backpacks.jpg" alt="" className="w-full h-full object-cover" data-id="element-952" />
          <div className="absolute inset-0 bg-gradient-to-r from-navy-900/95 via-navy-900/85 to-navy-900/50" data-id="element-953" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8" data-id="element-954">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center" data-id="element-955">
            {/* Left: Copy */}
            <div data-id="element-956">
              <span className="text-cg-orange-600 font-bold uppercase tracking-widest text-sm mb-4 block" data-id="element-957">
                Inventory
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6 leading-tight" data-id="element-958">
                Explore the Full Inventory
              </h2>
              <p className="text-steel-300 text-lg leading-relaxed mb-10 max-w-lg" data-id="element-959">
                Browse the complete catalogue across operational equipment
                categories. Submit an order request for review and fulfilment.
              </p>
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5" data-id="element-960">
                <a href={getShopUrl()} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center px-10 py-4 bg-cg-orange-600 hover:bg-cg-orange-500 text-white text-sm font-bold uppercase tracking-widest rounded-sm transition-colors" data-id="element-961">
                  Go to Inventory
                </a>
                <Link to="/manufacturing/catalogues" className="inline-flex items-center gap-2 text-sm font-bold text-steel-300 hover:text-white uppercase tracking-wider transition-colors" data-id="element-962">
                  <FileText className="w-4 h-4" data-id="element-963" /> Download Catalogues
                </Link>
              </div>
            </div>

            {/* Right: Decorative graphic */}
            <div className="hidden lg:flex items-center justify-center relative" data-id="element-964">
              <div className="relative w-full max-w-sm" data-id="element-965">
                <div className="absolute -top-4 -left-4 w-full h-full bg-cg-orange-600/10 rounded-sm" data-id="element-966"></div>
                <div className="relative bg-navy-800 border border-white/10 rounded-sm p-10" data-id="element-967">
                  <div className="flex items-center gap-4 mb-8" data-id="element-968">
                    <div className="w-12 h-12 bg-cg-orange-600/20 rounded-sm flex items-center justify-center" data-id="element-969">
                      <Package className="w-6 h-6 text-cg-orange-600" data-id="element-970" />
                    </div>
                    <div data-id="element-971">
                      <div className="text-xs font-bold text-cg-orange-600 uppercase tracking-widest" data-id="element-972">
                        Full Catalogue
                      </div>
                      <div className="text-white font-bold text-sm mt-0.5" data-id="element-973">
                        Operational Equipment
                      </div>
                    </div>
                  </div>
                  <div className="space-y-3" data-id="element-974">
                    {['Tactical Vests', 'Bags and Backpacks', 'Public Order Equipment', 'Clothing and Footwear', 'Training Equipment', 'Fire Retardants'].map((cat, i) => <div key={i} className="flex items-center gap-3 text-steel-400 text-sm" data-id="element-975">
                        <div className="w-1.5 h-1.5 bg-cg-orange-600 rounded-full flex-shrink-0" data-id="element-976"></div>
                        {cat}
                      </div>)}
                  </div>
                  <div className="mt-8 pt-6 border-t border-white/10" data-id="element-977">
                    <div className="text-xs text-steel-500 uppercase tracking-wider" data-id="element-978">
                      Order requests reviewed within 48 hours
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 5. Top Questions (FAQ) */}
      <section className="bg-slate-50 py-24 border-t border-steel-100" data-id="element-979">
        <div className="max-w-4xl mx-auto px-6 lg:px-8" data-id="element-980">
          <h2 className="text-3xl font-bold text-navy-900 mb-3" data-id="element-981">
            Top questions
          </h2>
          <p className="text-steel-600 mb-10" data-id="element-982">
            Quick answers to common procurement and ordering queries.
          </p>

          <div className="space-y-0 border-t border-steel-200" data-id="element-983">
            {faqs.map((faq, index) => <div key={index} className="border-b border-steel-200" data-id="element-984">
                <button onClick={() => toggleFaq(index)} className="w-full text-left py-5 flex justify-between items-center group focus:outline-none" aria-expanded={openFaqIndex === index} data-id="element-985">
                  <span className="font-bold text-navy-900 group-hover:text-cg-orange-600 transition-colors pr-8" data-id="element-986">
                    {faq.q}
                  </span>
                  <ChevronDown className={`w-5 h-5 text-steel-400 transform transition-transform duration-200 flex-shrink-0 ${openFaqIndex === index ? 'rotate-180 text-cg-orange-600' : ''}`} data-id="element-987" />
                </button>
                <div className={`overflow-hidden transition-all duration-300 ease-in-out ${openFaqIndex === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`} data-id="element-988">
                  <p className="pb-5 text-steel-600 text-sm leading-relaxed" data-id="element-989">
                    {faq.a}
                  </p>
                </div>
              </div>)}
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-8 items-start" data-id="element-990">
            <Link to="/faqs" className="inline-flex items-center justify-center px-8 py-4 bg-navy-900 text-white font-bold uppercase tracking-wider text-sm rounded-sm hover:bg-cg-orange-600 transition-colors" data-id="element-991">
              View all questions
            </Link>
            <div data-id="element-992">
              <Link to="/contact" className="inline-flex items-center justify-center px-8 py-4 border border-navy-900 text-navy-900 font-bold uppercase tracking-wider text-sm rounded-sm hover:bg-navy-900 hover:text-white transition-colors mb-3 w-full md:w-auto" data-id="element-993">
                Contact our team
              </Link>
              <p className="text-xs text-steel-500 leading-relaxed" data-id="element-994">
                If you have a specific question or requirement, we can advise
                the most appropriate next step.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 6. News Preview */}
      <section className="bg-white py-24 border-t border-steel-100" data-id="element-995">
        <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-996">
          <div className="flex justify-between items-end mb-12" data-id="element-997">
            <h2 className="text-3xl font-bold text-navy-900" data-id="element-998">
              News & Insights
            </h2>
            <Link to="/about/news" className="text-navy-900 font-bold uppercase tracking-wider text-sm hover:text-cg-orange-600 flex items-center gap-2" data-id="element-999">
              View all insights <ArrowRight className="w-4 h-4" data-id="element-1000" />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1001">
            {newsItems.map((item, index) => <div key={index} className="bg-white border border-steel-200 rounded-sm overflow-hidden flex flex-col hover:border-cg-orange-600 transition-colors group h-full" data-id="element-1002">
                {/* Image Area */}
                <div className="aspect-video bg-slate-100 overflow-hidden relative" data-id="element-1003">
                  <img src={item.image} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-1004" />
                  <div className="absolute top-4 left-4" data-id="element-1005">
                    <span className="bg-navy-900/90 text-white text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm" data-id="element-1006">
                      {item.date}
                    </span>
                  </div>
                </div>

                {/* Content Area */}
                <div className="p-6 flex flex-col flex-1" data-id="element-1007">
                  <h3 className="text-lg font-bold text-navy-900 mb-3 group-hover:text-cg-orange-600 transition-colors line-clamp-2" data-id="element-1008">
                    {item.title}
                  </h3>
                  <p className="text-sm text-steel-600 mb-6 flex-1 line-clamp-3" data-id="element-1009">
                    {item.excerpt}
                  </p>
                  <Link to="/about/news" className="text-sm font-bold text-navy-900 uppercase tracking-wider hover:text-cg-orange-600 transition-colors flex items-center gap-2 mt-auto" data-id="element-1010">
                    Read More <ArrowRight className="w-3 h-3" data-id="element-1011" />
                  </Link>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* 7. Facts Strip */}

      {/* 8. Contact CTA */}
      <PartnerCTA data-id="element-1012" />
    </>;
}